import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { resourcesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Download, Loader2, FileText, BookOpen, BarChart3, Mail, Lightbulb, Layers } from 'lucide-react';
import { CATEGORY_COLOR_MAP } from '../constants/resourceCategories';

const NAVY   = '#1e3a5f';
const ORANGE = '#ea580c';
const API    = process.env.REACT_APP_BACKEND_URL;

// ── Content block renderers ──────────────────────────────────────────────────

function BlockHeading({ block }) {
  const anchor = block.content.toLowerCase().replace(/[\s-–]+/g, '-').replace(/[^a-z0-9-]/g, '');
  return (
    <div id={anchor} className="scroll-mt-4 pt-4">
      <h2 className="text-xl font-black pb-2 mb-1" style={{ fontFamily: 'var(--font-display)', color: NAVY, borderBottom: '2px solid #e2e8f0' }}>
        {block.content}
      </h2>
    </div>
  );
}

function BlockSubheading({ block }) {
  return <h3 className="text-sm font-bold mt-4 mb-1.5" style={{ color: '#334155' }}>{block.content}</h3>;
}

function BlockParagraph({ block }) {
  return <p className="text-sm leading-relaxed" style={{ color: '#475569' }}>{block.content}</p>;
}

function BlockCallout({ block }) {
  return (
    <div className="rounded-lg px-4 py-3" style={{ background: '#f0f9ff', borderLeft: '3px solid #0ea5e9', color: '#0c4a6e' }}>
      {block.title && <p className="font-bold text-sm mb-1.5" style={{ color: '#075985' }}>{block.title}</p>}
      <p className="text-sm leading-relaxed">{block.content}</p>
    </div>
  );
}

function BlockInternalNote({ block }) {
  return (
    <div className="rounded-lg overflow-hidden" style={{ border: '1px solid #fde68a' }}>
      <div className="px-4 py-2" style={{ background: '#fef3c7' }}>
        <p className="text-xs font-bold uppercase tracking-wide" style={{ color: '#92400e' }}>C4P Guidance</p>
      </div>
      <div className="px-4 py-3 text-sm whitespace-pre-line" style={{ background: '#fffbeb', color: '#78350f' }}>
        {block.content}
      </div>
    </div>
  );
}

function BlockBullets({ block }) {
  return (
    <ul className="space-y-1.5 pl-4">
      {(block.items || []).map((item, i) => {
        const text   = typeof item === 'string' ? item : item.text;
        const active = typeof item === 'string' ? true : (item.active !== false);
        if (!active || !text) return null;
        return (
          <li key={i} className="text-sm flex gap-2" style={{ color: '#475569' }}>
            <span className="flex-shrink-0 mt-0.5 font-bold" style={{ color: ORANGE }}>•</span>
            <span>{text}</span>
          </li>
        );
      })}
    </ul>
  );
}

function BlockNumbered({ block }) {
  return (
    <ol className="space-y-1.5 pl-4 list-decimal">
      {(block.items || []).map((item, i) => {
        const text   = typeof item === 'string' ? item : item.text;
        const active = typeof item === 'string' ? true : (item.active !== false);
        if (!active || !text) return null;
        return <li key={i} className="text-sm" style={{ color: '#475569' }}>{text}</li>;
      })}
    </ol>
  );
}

function BlockEmailTemplate({ block }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(`Subject: ${block.subject}\n\n${block.content}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid #e2e8f0' }}>
      <div className="flex items-center justify-between px-4 py-3" style={{ background: NAVY }}>
        <div>
          <p className="text-xs font-bold uppercase tracking-wide mb-0.5" style={{ color: 'rgba(255,255,255,0.6)' }}>Email Template</p>
          <p className="text-sm font-semibold text-white">Subject: {block.subject}</p>
        </div>
        <button onClick={copy} className="text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors flex-shrink-0"
          style={{ background: copied ? '#16a34a' : 'rgba(255,255,255,0.15)', color: 'white' }}>
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <div className="px-4 py-3 text-sm whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto"
        style={{ background: '#f8fafc', color: '#334155', fontSize: '12px' }}>
        {block.content}
      </div>
    </div>
  );
}

function BlockTable({ block }) {
  return (
    <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid #e2e8f0' }}>
      <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
        {block.headers && (
          <thead>
            <tr style={{ background: NAVY }}>
              {block.headers.map((h, i) => (
                <th key={i} className="px-4 py-3 text-left text-xs font-bold" style={{ color: 'white' }}>{h}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {(block.rows || []).map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 text-sm align-top" style={{ color: '#475569' }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderBlock(block, i) {
  switch (block.type) {
    case 'heading':        return <BlockHeading key={i} block={block} />;
    case 'subheading':     return <BlockSubheading key={i} block={block} />;
    case 'paragraph':      return <BlockParagraph key={i} block={block} />;
    case 'callout':        return <BlockCallout key={i} block={block} />;
    case 'internal_note':  return <BlockInternalNote key={i} block={block} />;
    case 'bullets':        return <BlockBullets key={i} block={block} />;
    case 'numbered':       return <BlockNumbered key={i} block={block} />;
    case 'email_template': return <BlockEmailTemplate key={i} block={block} />;
    case 'table':          return <BlockTable key={i} block={block} />;
    case 'decision_tree':  return <BlockDecisionTree key={i} block={block} />;
    case 'plan_comparison':return <BlockPlanComparison key={i} block={block} />;
    case 'scenario_cards': return <BlockScenarioCards key={i} block={block} />;
    case 'divider':        return <hr key={i} className="my-4" style={{ borderColor: '#e2e8f0' }} />;
    default:               return null;
  }
}

// ── Table of Contents ─────────────────────────────────────────────────────────
function TableOfContents({ blocks }) {
  const headings = (blocks || []).filter(b => b.type === 'heading');
  if (headings.length <= 1) return null;
  return (
    <nav className="rounded-xl border bg-white p-4 space-y-1 sticky top-4">
      <p className="text-xs font-bold uppercase tracking-wide mb-3" style={{ color: NAVY }}>Contents</p>
      {headings.map((b, i) => {
        const anchor = b.content.toLowerCase().replace(/[\s-–]+/g, '-').replace(/[^a-z0-9-]/g, '');
        return (
          <a key={i} href={`#${anchor}`}
            className="block text-xs py-1 px-2 rounded transition-colors hover:bg-orange-50"
            style={{ color: '#475569' }}
            onClick={e => { e.preventDefault(); document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' }); }}>
            {b.content}
          </a>
        );
      })}
    </nav>
  );
}

// ── Decision Tree Block ───────────────────────────────────────────────────────
const PLAN_COLORS = {
  standard: { bg: '#ecfeff', border: '#0891b2', text: '#0e7490', badge: '#0891b2' },
  advanced: { bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8', badge: '#3b82f6' },
  mtpro:    { bg: '#f5f3ff', border: '#7c3aed', text: '#6d28d9', badge: '#7c3aed' },
};

function BlockDecisionTree({ block }) {
  const questions = block.questions || [];
  return (
    <div className="space-y-3">
      {questions.map((q, i) => {
        const yesIsOutcome = typeof q.yes === 'object' && q.yes.outcome;
        const noIsOutcome  = typeof q.no  === 'object' && q.no.outcome;
        const yesColors = yesIsOutcome ? PLAN_COLORS[q.yes.color] : null;
        const noColors  = noIsOutcome  ? PLAN_COLORS[q.no.color]  : null;
        return (
          <div key={q.id} className="rounded-xl border bg-white overflow-hidden">
            {/* Question header */}
            <div className="px-4 py-3 flex items-start gap-3" style={{ background: NAVY }}>
              <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black text-white flex-shrink-0 mt-0.5"
                style={{ background: 'rgba(255,255,255,0.25)' }}>Q{i + 1}</span>
              <p className="font-semibold text-sm leading-relaxed text-white">{q.text}</p>
            </div>
            {/* YES / NO paths */}
            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x" style={{ borderTop: '1px solid #e2e8f0' }}>
              {/* YES */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-black px-2 py-0.5 rounded" style={{ background: '#dcfce7', color: '#166534' }}>YES</span>
                  <span className="text-xs" style={{ color: '#64748b' }}>→</span>
                  {yesIsOutcome && (
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ background: yesColors.badge }}>
                      {q.yes.outcome}
                    </span>
                  )}
                </div>
                {yesIsOutcome && q.yes.detail && (
                  <p className="text-xs ml-0" style={{ color: '#475569' }}>{q.yes.detail}</p>
                )}
                {!yesIsOutcome && (
                  <p className="text-xs" style={{ color: '#64748b' }}>Continue to Question {parseInt(String(q.no).replace('q',''))}</p>
                )}
              </div>
              {/* NO */}
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-black px-2 py-0.5 rounded" style={{ background: '#fee2e2', color: '#991b1b' }}>NO</span>
                  <span className="text-xs" style={{ color: '#64748b' }}>→</span>
                  {noIsOutcome && (
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full text-white" style={{ background: noColors.badge }}>
                      {q.no.outcome}
                    </span>
                  )}
                </div>
                {noIsOutcome && q.no.detail && (
                  <p className="text-xs" style={{ color: '#475569' }}>{q.no.detail}</p>
                )}
                {!noIsOutcome && (
                  <p className="text-xs" style={{ color: '#64748b' }}>Continue to Question {parseInt(String(q.no).replace('q',''))}</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Plan Comparison Block ─────────────────────────────────────────────────────
function BlockPlanComparison({ block }) {
  const plans  = block.plans  || [];
  const colors = block.colors || ['#0891b2', '#3b82f6', '#7c3aed'];
  const rows   = block.rows   || [];
  const isPos  = (v) => typeof v === 'string' && (v.startsWith('✓') || v === 'Available');
  const isNeg  = (v) => typeof v === 'string' && (v.startsWith('✗') || v === 'Not included');
  const isDash = (v) => v === '-';
  return (
    <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid #e2e8f0' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <thead>
          <tr>
            <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '12px', fontWeight: 700,
                         background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', width: '28%' }}>
            </th>
            {plans.map((plan, i) => (
              <th key={i} style={{ padding: '10px 14px', textAlign: 'center', fontSize: '13px',
                                   fontWeight: 900, color: 'white', background: colors[i],
                                   border: `1px solid ${colors[i]}` }}>
                {plan}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? 'white' : '#fafafa' }}>
              <td style={{ padding: '10px 14px', fontSize: '12px', fontWeight: 700,
                           textAlign: 'left', color: '#334155', border: '1px solid #e2e8f0' }}>
                {row.label}
              </td>
              {(row.values || []).map((val, j) => (
                <td key={j} style={{
                  padding: '10px 14px', fontSize: '12px', textAlign: 'center',
                  border: '1px solid #e2e8f0',
                  color: isPos(val) ? '#16a34a' : isNeg(val) ? '#94a3b8' : isDash(val) ? '#94a3b8' : '#334155',
                  fontWeight: isPos(val) ? 700 : 400,
                }}>
                  {val}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Scenario Cards Block ──────────────────────────────────────────────────────
function BlockScenarioCards({ block }) {
  const cards = block.cards || [];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" style={{ alignItems: 'stretch' }}>
      {cards.map((card, i) => {
        const colors = PLAN_COLORS[card.color] || PLAN_COLORS.standard;
        return (
          <div key={i} className="rounded-xl flex flex-col overflow-hidden"
            style={{ border: `1.5px solid ${colors.border}` }}>

            {/* FIXED-HEIGHT HEADER: label (left) + pill (right) - always exactly one line tall.
                The border-bottom IS the divider. Guaranteed same position in every card. */}
            <div className="flex items-center justify-between gap-3 px-4 py-3 flex-shrink-0"
              style={{ background: colors.bg, borderBottom: `1.5px solid ${colors.border}` }}>
              <span className="text-xs font-black uppercase tracking-wider" style={{ color: colors.badge }}>
                Scenario {card.label}
              </span>
              <span className="text-xs font-bold px-3 py-1 rounded-full text-white flex-shrink-0"
                style={{ background: colors.badge, fontSize: '11px' }}>
                {card.recommendation}
              </span>
            </div>

            {/* VARIABLE CONTENT: everything below the divider */}
            <div className="flex flex-col flex-1 px-4 py-4 gap-3" style={{ background: colors.bg }}>
              <p className="text-sm font-bold leading-snug" style={{ color: '#1e293b' }}>
                {card.scenario}
              </p>
              {card.why && (
                <p className="text-xs leading-relaxed" style={{ color: '#475569' }}>
                  {card.why}
                </p>
              )}
            </div>

          </div>
        );
      })}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ResourceDetailPage() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [resource, setResource]     = useState(null);
  const [loading, setLoading]       = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [showPreparedFor, setShowPreparedFor] = useState(false);
  const [preparedFor, setPreparedFor] = useState('');

  const profileComplete = !!(user?.psm_full_name && user?.psm_direct_email);

  useEffect(() => {
    resourcesAPI.get(id)
      .then(r => setResource(r.data))
      .catch(() => toast.error('Failed to load resource'))
      .finally(() => setLoading(false));
  }, [id]);

  const doDownload = async (partnerName) => {
    setDownloading(true);
    setShowPreparedFor(false);
    try {
      const token = localStorage.getItem('c4p_access_token');
      const url = `${API}/api/resources/${id}/pdf${partnerName ? `?prepared_for=${encodeURIComponent(partnerName)}` : ''}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const safe = (resource?.title || 'resource').replace(/[\\/:*?"<>|·•]/g, '-').trim();
      const a = document.createElement('a'); a.href = objUrl; a.download = `${safe}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to download PDF'); }
    finally { setDownloading(false); }
  };

  const handleDownloadClick = () => {
    setPreparedFor('');
    setShowPreparedFor(true);
  };

  if (loading) return (
    <div className="max-w-5xl mx-auto space-y-4">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-32 w-full rounded-2xl" />
      <Skeleton className="h-96 w-full rounded-2xl" />
    </div>
  );

  if (!resource) return <p>Resource not found.</p>;

  const catColor = CATEGORY_COLOR_MAP[resource.category] || '#64748b';

  return (
    <div className="max-w-5xl mx-auto">
      {/* Back + Download */}
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate('/resources')} className="gap-1.5">
            <ArrowLeft size={14} /> Resources
          </Button>
          <span style={{ color: 'hsl(var(--muted-fg))' }}>/</span>
          <span className="text-sm" style={{ color: 'hsl(var(--muted-fg))' }}>{resource.category}</span>
        </div>
        <Button onClick={handleDownloadClick} disabled={downloading} className="gap-2"
          style={{ background: NAVY, color: 'white' }} data-testid="resource-download-pdf">
          {downloading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Download PDF
        </Button>
      </div>

      {/* Profile completion prompt */}
      {!profileComplete && (
        <div className="rounded-xl p-3 mb-4 flex items-center justify-between gap-3 flex-wrap"
          style={{ background: '#fef3c7', border: '1px solid #fde68a' }}>
          <p className="text-sm" style={{ color: '#92400e' }}>
            Complete your profile to personalise PDF downloads with your contact details.
          </p>
          <button onClick={() => navigate('/profile')} className="text-sm font-semibold flex-shrink-0"
            style={{ color: '#92400e', textDecoration: 'underline' }}>
            Go to profile →
          </button>
        </div>
      )}

      {/* "Prepared for" dialog */}
      {showPreparedFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowPreparedFor(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="font-bold text-lg" style={{ color: NAVY }}>Download PDF</h3>
            <div className="space-y-1.5">
              <label className="text-sm font-semibold" style={{ color: NAVY }}>
                Prepared for <span className="font-normal text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>- optional</span>
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm"
                placeholder="e.g. Acme Corporation"
                value={preparedFor}
                onChange={e => setPreparedFor(e.target.value)}
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') doDownload(preparedFor); }}
              />
              <p className="text-xs" style={{ color: 'hsl(var(--muted-fg))' }}>
                Adds "Prepared for: [name]" to the PDF header. Leave blank to skip.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowPreparedFor(false)}
                className="flex-1 px-4 py-2 rounded-lg border text-sm font-semibold">
                Cancel
              </button>
              <button onClick={() => doDownload(preparedFor)}
                className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white"
                style={{ background: NAVY }}>
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header card */}
      <div className="rounded-2xl overflow-hidden mb-6 shadow-sm" style={{ border: '1px solid #e2e8f0' }}>
        <div className="px-8 py-6" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #2d5a8e 100%)` }}>
          <span className="text-xs font-bold px-2.5 py-1 rounded-full mb-3 inline-block"
            style={{ background: catColor, color: 'white' }}>
            {resource.category}
          </span>
          <h1 className="text-2xl font-black text-white mt-2 mb-2" style={{ fontFamily: 'var(--font-display)' }}>
            {resource.title}
          </h1>
          {resource.description && (
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{resource.description}</p>
          )}
        </div>
      </div>

      {/* Two-column layout: TOC + content */}
      <div className="flex flex-col lg:flex-row gap-6 items-start">
        {/* TOC - hidden on mobile/tablet, sticky on desktop */}
        <div className="hidden lg:block w-52 flex-shrink-0">
          <TableOfContents blocks={resource.content_blocks} />
        </div>
        {/* Mobile TOC inline (collapsible) */}
        <div className="lg:hidden w-full">
          <details className="rounded-xl border bg-white mb-4">
            <summary className="px-4 py-3 text-sm font-semibold cursor-pointer" style={{ color: NAVY }}>
              Contents
            </summary>
            <div className="px-4 pb-3">
              <TableOfContents blocks={resource.content_blocks} />
            </div>
          </details>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0 bg-white rounded-2xl border p-8 space-y-4 shadow-sm">
          {(resource.content_blocks || []).map((block, i) => renderBlock(block, i))}
        </div>
      </div>
    </div>
  );
}
